import { graphGetJson } from "../../email/services/graph";
import type { RefreshTokenFn } from "../../../shared/api/fetchWithRefresh";
import type { CalEvent, GraphEventDto } from "../types";
import { mapEvent } from "../domain/mapEvent";

const EVENT_SELECT = [
  "id", "subject", "start", "end", "isAllDay", "location", "organizer", "attendees", "isOrganizer",
  "responseStatus", "responseRequested", "showAs", "isCancelled", "onlineMeeting", "webLink", "type",
  "seriesMasterId", "hasAttachments", "isDraft", "allowNewTimeProposals", "bodyPreview", "changeKey",
].join(",");

/** Graph's calendarView returns only 10 items unless asked for more. */
export const CALENDAR_PAGE_SIZE = 100;
/** Beyond this many pages the result is reported as partial. */
export const CALENDAR_MAX_PAGES = 5;

interface CalendarViewPage {
  readonly value?: GraphEventDto[];
  readonly "@odata.nextLink"?: string;
}

/**
 * Every event between `start` and `end`, following Graph's paging. Times are
 * requested in UTC and mapped to local instants; unreadable items are dropped.
 */
export async function fetchCalendarView(
  token: string,
  refresh: RefreshTokenFn | undefined,
  start: Date,
  end: Date,
): Promise<{ events: CalEvent[]; truncated: boolean }> {
  const url =
    `/me/calendarView?startDateTime=${encodeURIComponent(start.toISOString())}` +
    `&endDateTime=${encodeURIComponent(end.toISOString())}` +
    `&$select=${EVENT_SELECT}&$orderby=start/dateTime&$top=${CALENDAR_PAGE_SIZE}`;
  const { items, next } = await readPages(url, CALENDAR_MAX_PAGES, token, refresh);

  // Graph pages with $skip, so an event that moves while the pages are read can come back twice; keep the first.
  const events: CalEvent[] = [];
  const seen = new Set<string>();
  for (const item of items) {
    const event = mapEvent(item);
    if (!event || seen.has(event.id)) continue;
    seen.add(event.id);
    events.push(event);
  }
  return { events, truncated: Boolean(next) };
}

/**
 * Read `url` and the pages after it, one after another (each page's link comes
 * from the one before), at most `pagesLeft` pages. `next` is the link to the
 * first page not read, if there is one.
 */
async function readPages(
  url: string,
  pagesLeft: number,
  token: string,
  refresh: RefreshTokenFn | undefined,
): Promise<{ items: GraphEventDto[]; next: string | undefined }> {
  const page = await graphGetJson<CalendarViewPage>(url, token, refresh, { Prefer: 'outlook.timezone="UTC"' });
  const items = page.value ?? [];
  const next = page["@odata.nextLink"];
  if (!next || pagesLeft <= 1) return { items, next };
  const rest = await readPages(next, pagesLeft - 1, token, refresh);
  return { items: [...items, ...rest.items], next: rest.next };
}
